import { useEffect, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { ShoppingBag, ShieldCheck, Tag, Loader2, ArrowLeft, Building2, ChevronRight } from 'lucide-react'
import type { ApiError } from '@/lib/api'
import { api } from '@/lib/api'
import { NovaPoshtaFields } from '@/components/NovaPoshtaFields'
import { UkrposhtaFields } from '@/components/UkrposhtaFields'
import { FopRequisitesModal } from '@/components/FopRequisitesModal'
import { paymentLabel } from '@/lib/payment'
import { haptic, showBackButton } from '@/lib/telegram'
import { useAppStore } from '@/lib/store'
import { useT } from '@/lib/i18n'
import { useStoreTheme } from '@/lib/useStoreTheme'

interface Profile {
  name: string
  email: string
  phone: string
  saved_delivery_type: string
  saved_city: string
  saved_city_ref: string
  saved_warehouse: string
  saved_warehouse_ref: string
}
interface Cart {
  count: number
  total_uah: number
  product_count: number
  service_count: number
  requires_delivery: boolean
}
interface PaymentMethods { methods: string[] }

interface CheckoutResult {
  order_id?: number
  payment_url?: string
  message?: string
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  error,
  required,
  placeholder,
  id,
  isDarkTheme = false,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  error?: string
  required?: boolean
  placeholder?: string
  id?: string
  isDarkTheme?: boolean
}) {
  const fieldId = id || `field-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`
  return (
    <div>
      <label
        htmlFor={fieldId}
        className={`block text-sm font-medium mb-1 ${
          isDarkTheme ? 'text-slate-300' : 'text-gray-700'
        }`}
      >
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      <input
        id={fieldId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none ${
          isDarkTheme
            ? error
              ? 'border-rose-500 bg-[#1a202c] text-[#f8f3ea]'
              : 'border-[#3a4352] bg-[#1a202c] text-[#f8f3ea] placeholder-slate-500 focus:border-amber-400 focus:ring-2 focus:ring-amber-500/20'
            : error
              ? 'border-red-400'
              : 'border-gray-300 bg-white text-slate-900 focus:ring-2 focus:ring-brand-500'
        }`}
      />
      {error && <p className="mt-0.5 text-xs text-red-500">{error}</p>}
    </div>
  )
}

export function CheckoutPage() {
  const lang = useAppStore((s) => s.lang)
  const setCartCount = useAppStore((s) => s.setCartCount)
  const navigate = useNavigate()
  const T = useT('checkout')
  const tNav = useT('nav')
  const theme = useStoreTheme()

  const { data: cart } = useQuery<Cart>({
    queryKey: ['cart'],
    queryFn: () => api.get<Cart>('/cart'),
  })
  const { data: profile } = useQuery<Profile>({
    queryKey: ['profile'],
    queryFn: () => api.get<Profile>('/me'),
  })
  const { data: pmData } = useQuery<PaymentMethods>({
    queryKey: ['payment-methods'],
    queryFn: () => api.get<PaymentMethods>('/payment/methods'),
  })
  const { data: settings } = useQuery<{ phone_placeholder?: string }>({
    queryKey: ['settings'],
    queryFn: () => api.get('/settings'),
  })
  const { data: requisites } = useQuery<{ enabled: boolean; bank_iban?: string; bank_card?: string }>({
    queryKey: ['payment-requisites'],
    queryFn: () => api.get('/payment/requisites'),
  })

  const [name, setName] = useState('')
  const [contact, setContact] = useState('')
  const [deliveryType, setDeliveryType] = useState('')
  const [city, setCity] = useState('')
  const [cityRef, setCityRef] = useState('')
  const [delivery, setDelivery] = useState('')
  const [warehouseRef, setWarehouseRef] = useState('')
  const [comment, setComment] = useState('')
  const [promo, setPromo] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('')
  const [showFopModal, setShowFopModal] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [promoValidation, setPromoValidation] = useState<{
    valid: boolean
    discount_percent?: number | null
    discount_fixed_uah?: number | null
    code?: string
  } | null>(null)

  const promoMutation = useMutation<
    { valid: boolean; discount_percent?: number | null; discount_fixed_uah?: number | null; error?: string },
    ApiError,
    string
  >({
    mutationFn: (code: string) => api.post('/promo/validate', { code }),
    onSuccess: (res, code) => {
      if (res.valid) {
        setPromoValidation({
          valid: true,
          discount_percent: res.discount_percent,
          discount_fixed_uah: res.discount_fixed_uah,
          code,
        })
        setFieldErrors((prev) => {
          const next = { ...prev }
          delete next.promo
          return next
        })
        haptic('success')
      } else {
        setPromoValidation(null)
        setFieldErrors((prev) => ({ ...prev, promo: T.errors.promo_invalid }))
        haptic('error')
      }
    },
    onError: () => {
      setPromoValidation(null)
      setFieldErrors((prev) => ({ ...prev, promo: T.errors.promo_invalid }))
      haptic('error')
    },
  })

  const requiresDelivery = Boolean(cart?.requires_delivery)
  const serviceOnlyCheckout = Boolean(cart && cart.product_count === 0 && cart.service_count > 0)

  // Pre-fill from profile
  useEffect(() => {
    if (!profile) return
    if (!name && profile.name) setName(profile.name) // eslint-disable-line react-hooks/set-state-in-effect
    if (!contact && profile.phone) setContact(profile.phone)
    else if (!contact && profile.email) setContact(profile.email)
    if (!deliveryType && profile.saved_delivery_type) setDeliveryType(profile.saved_delivery_type)
    if (!city && profile.saved_city) setCity(profile.saved_city)
    if (!cityRef && profile.saved_city_ref) setCityRef(profile.saved_city_ref)
    if (!delivery && profile.saved_warehouse) setDelivery(profile.saved_warehouse)
    if (!warehouseRef && profile.saved_warehouse_ref) setWarehouseRef(profile.saved_warehouse_ref)
  }, [profile]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (deliveryType !== 'nova_poshta') {
      setCityRef('') // eslint-disable-line react-hooks/set-state-in-effect
      setWarehouseRef('')
    }
  }, [deliveryType])

  // Default payment method
  useEffect(() => {
    if (pmData?.methods.length && !paymentMethod) {
      setPaymentMethod(pmData.methods[0]) // eslint-disable-line react-hooks/set-state-in-effect
    }
  }, [pmData]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return showBackButton(() => {
      void navigate({ to: '/cart' })
    })
  }, [navigate])

  const submitMutation = useMutation<CheckoutResult, ApiError>({
    mutationFn: () =>
      api.post<CheckoutResult>('/checkout', {
        client_name: name.trim(),
        contact: contact.trim(),
        city: requiresDelivery ? city.trim() : '',
        delivery: requiresDelivery ? delivery.trim() : '',
        comment: comment.trim(),
        promo_code: promo.trim(),
        payment_method: paymentMethod,
      }),
    onSuccess: (result) => {
      haptic('success')
      setCartCount(0)
      if (result.payment_url) {
        window.location.href = result.payment_url
      } else {
        void navigate({ to: serviceOnlyCheckout ? '/bookings' : '/orders' })
      }
    },
    onError: (err) => {
      haptic('error')
      const detail = err.message
      if (detail === 'contact_invalid') {
        setFieldErrors((prev) => ({ ...prev, contact: T.errors.contact_invalid }))
      } else if (detail === 'promo_invalid') {
        setFieldErrors((prev) => ({ ...prev, promo: T.errors.promo_invalid }))
      }
    },
  })

  function validate() {
    const errs: Record<string, string> = {}
    if (!name.trim()) errs.name = T.errors.name_required
    if (!contact.trim()) errs.contact = T.errors.contact_required
    if (requiresDelivery && !city.trim()) errs.city = T.errors.city_required
    if (requiresDelivery && !delivery.trim()) errs.delivery = T.errors.delivery_required
    setFieldErrors(errs)
    return Object.keys(errs).length === 0
  }

  function handleSubmit() {
    if (!validate()) return
    haptic('medium')
    submitMutation.mutate()
  }

  if (!cart) return <p className="p-4 text-gray-500">{T.loading}</p>
  if (cart.count === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-gray-400">
        <ShoppingBag className="h-12 w-12 mb-3 opacity-40" />
        <p className="mb-4">{T.emptyCart}</p>
        <Link
          to="/catalog"
          className="rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 transition"
        >
          {T.goToCatalog}
        </Link>
      </div>
    )
  }

  const methods = pmData?.methods ?? []

  const inputClass = `w-full rounded-xl border px-4 py-3 text-sm focus:outline-none transition ${
    theme.isDarkTheme
      ? 'border-[#3a4352] bg-[#1a202c] text-[#f8f3ea] placeholder-slate-500 focus:border-amber-400 focus:ring-2 focus:ring-amber-500/20'
      : 'border-gray-300 bg-white text-slate-900 focus:ring-2 focus:ring-brand-500'
  }`

  const labelClass = `block text-sm font-medium mb-1 ${
    theme.isDarkTheme ? 'text-slate-300' : 'text-gray-700'
  }`

  return (
    <div className={`pb-32 px-4 ${theme.isDarkTheme ? 'min-h-screen bg-[#1a202c] text-[#f8f3ea]' : ''}`}>
      <div
        className={`pt-3 pb-3 flex items-center justify-between border-b mb-4 ${
          theme.isDarkTheme ? 'border-[#2d3748]' : 'border-gray-100'
        }`}
      >
        <div className="flex items-center gap-2.5">
          <Link
            to="/cart"
            className={`inline-flex items-center justify-center -ml-1.5 p-2 rounded-xl active:scale-95 transition ${
              theme.isDarkTheme
                ? 'text-slate-300 hover:text-white hover:bg-[#242c38]'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
            aria-label={T.backToCart}
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className={`text-lg font-bold leading-tight ${theme.isDarkTheme ? 'text-[#f8f3ea]' : 'text-gray-900'}`}>
              {T.title}
            </h1>
            <p className="text-[11px] text-slate-400">
              {tNav.cart} → {T.title}
            </p>
          </div>
        </div>
        <Link
          to="/catalog"
          className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition ${
            theme.isDarkTheme
              ? 'text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30'
              : 'text-brand-600 hover:text-brand-700 bg-brand-50 hover:bg-brand-100'
          }`}
        >
          {T.addItems}
        </Link>
      </div>

      <div className="space-y-4">
        <Field
          label={T.name}
          value={name}
          onChange={setName}
          error={fieldErrors.name}
          required
          isDarkTheme={theme.isDarkTheme}
        />
        <Field
          label={T.contact}
          value={contact}
          onChange={setContact}
          placeholder={settings?.phone_placeholder || '+380 50 123 45 67'}
          error={fieldErrors.contact}
          required
          isDarkTheme={theme.isDarkTheme}
        />
        {requiresDelivery && (
          <>
            <div>
              <label className={labelClass}>{T.deliveryType}</label>
              <select
                value={deliveryType}
                onChange={(e) => setDeliveryType(e.target.value)}
                className={inputClass}
              >
                {T.deliveryTypes.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </div>
            {deliveryType === 'nova_poshta' ? (
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>
                    {T.city}<span className="text-red-400 ml-0.5">*</span>
                  </label>
                  <NovaPoshtaFields
                    lang={lang}
                    city={city}
                    cityRef={cityRef}
                    warehouse={delivery}
                    warehouseRef={warehouseRef}
                    onCityChange={(nextCity, nextRef) => {
                      setCity(nextCity)
                      setCityRef(nextRef)
                    }}
                    onWarehouseChange={(nextWarehouse, nextRef) => {
                      setDelivery(nextWarehouse)
                      setWarehouseRef(nextRef)
                    }}
                  />
                  {fieldErrors.city && <p className="mt-1 text-xs text-red-500">{fieldErrors.city}</p>}
                  {fieldErrors.delivery && <p className="mt-1 text-xs text-red-500">{fieldErrors.delivery}</p>}
                </div>
              </div>
            ) : deliveryType === 'ukrposhta' ? (
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>
                    {T.delivery}<span className="text-red-400 ml-0.5">*</span>
                  </label>
                  <UkrposhtaFields
                    lang={lang}
                    city={city}
                    warehouse={delivery}
                    onCityChange={setCity}
                    onWarehouseChange={setDelivery}
                  />
                  {fieldErrors.city && <p className="mt-1 text-xs text-red-500">{fieldErrors.city}</p>}
                  {fieldErrors.delivery && <p className="mt-1 text-xs text-red-500">{fieldErrors.delivery}</p>}
                </div>
              </div>
            ) : (
              <>
                <Field
                  label={T.city}
                  value={city}
                  onChange={setCity}
                  error={fieldErrors.city}
                  required
                  isDarkTheme={theme.isDarkTheme}
                />
                <Field
                  label={T.delivery}
                  value={delivery}
                  onChange={setDelivery}
                  error={fieldErrors.delivery}
                  required
                  isDarkTheme={theme.isDarkTheme}
                />
              </>
            )}
          </>
        )}
        <Field
          label={T.comment}
          value={comment}
          onChange={setComment}
          isDarkTheme={theme.isDarkTheme}
        />

        {/* Promo code */}
        <div>
          <label className={labelClass}>{T.promo}</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Tag className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={promo}
                onChange={(e) => {
                  const val = e.target.value.toUpperCase()
                  setPromo(val)
                  if (promoValidation && promoValidation.code !== val) {
                    setPromoValidation(null)
                  }
                  if (fieldErrors.promo) {
                    setFieldErrors((prev) => {
                      const next = { ...prev }
                      delete next.promo
                      return next
                    })
                  }
                }}
                placeholder={T.promo}
                className={`w-full rounded-xl border pl-9 pr-4 py-3 text-sm uppercase tracking-wider focus:outline-none ${
                  theme.isDarkTheme
                    ? fieldErrors.promo
                      ? 'border-rose-500 bg-[#1a202c] text-[#f8f3ea]'
                      : 'border-[#3a4352] bg-[#1a202c] text-[#f8f3ea] placeholder-slate-500 focus:border-amber-400'
                    : fieldErrors.promo
                      ? 'border-red-400 bg-white text-slate-900'
                      : 'border-gray-300 bg-white text-slate-900 focus:ring-2 focus:ring-brand-500'
                }`}
              />
            </div>
            {promo.trim() && (
              <button
                type="button"
                onClick={() => promoMutation.mutate(promo.trim())}
                disabled={
                  promoMutation.isPending ||
                  (promoValidation?.valid === true && promoValidation.code === promo.trim())
                }
                className={`rounded-xl px-4 py-3 text-sm font-medium disabled:opacity-50 transition ${
                  theme.isDarkTheme
                    ? 'bg-[#242c38] hover:bg-[#2d3748] text-slate-200 border border-[#3a4352]'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                }`}
              >
                {promoMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : promoValidation?.valid && promoValidation.code === promo.trim() ? (
                  '✓'
                ) : (
                  T.applyPromo
                )}
              </button>
            )}
          </div>
          {fieldErrors.promo && (
            <p className="mt-1 text-xs text-rose-500">{fieldErrors.promo}</p>
          )}
          {promoValidation?.valid && promoValidation.code === promo.trim() && (
            <p className="mt-1 text-xs text-emerald-500 font-medium">
              {T.promoApplied} (
              {promoValidation.discount_percent
                ? `-${promoValidation.discount_percent}%`
                : `-${promoValidation.discount_fixed_uah} UAH`}
              )
            </p>
          )}
        </div>

        {/* Payment method picker */}
        {methods.length > 1 ? (
          <div>
            <label className={labelClass}>{T.payment}</label>
            <div className="flex flex-wrap gap-2">
              {methods.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    setPaymentMethod(m)
                    haptic('light')
                  }}
                  className={`rounded-xl border px-4 py-2.5 text-sm font-medium transition ${
                    paymentMethod === m
                      ? theme.isDarkTheme
                        ? 'border-[#c9a24b] bg-[#c9a24b] text-[#17120f] font-bold shadow-sm'
                        : 'bg-brand-600 text-white border-brand-600 shadow-sm'
                      : theme.isDarkTheme
                        ? 'border-[#3a4352] bg-[#242c38] text-slate-300 hover:bg-[#2d3748]'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {paymentLabel(m, lang, theme.isKlodchik)}
                </button>
              ))}
            </div>
          </div>
        ) : methods.length === 1 ? (
          <div
            className={`rounded-xl border p-3.5 ${
              theme.isDarkTheme
                ? 'border-[#2d3748] bg-[#242c38]'
                : 'border-gray-200 bg-gray-50'
            }`}
          >
            <p className="text-xs text-slate-400 font-medium mb-1">{T.payment}</p>
            <p className={`text-sm font-semibold flex items-center gap-1.5 ${theme.isDarkTheme ? 'text-[#f8f3ea]' : 'text-gray-800'}`}>
              <span>💳</span> {paymentLabel(methods[0], lang, theme.isKlodchik)}
            </p>
          </div>
        ) : (
          <div
            className={`rounded-xl border border-dashed p-3.5 text-xs ${
              theme.isDarkTheme
                ? 'border-[#3a4352] bg-[#1a202c] text-slate-400'
                : 'border-gray-200 bg-gray-50/70 text-gray-500'
            }`}
          >
            {T.manualPaymentInfo}
          </div>
        )}

        {/* FOP Payment banner when manual is selected */}
        {(paymentMethod === 'manual' || (methods.length === 1 && methods[0] === 'manual')) && (
          requisites?.bank_iban ? (
            <div
              className={`rounded-2xl border p-3.5 space-y-2.5 transition ${
                theme.isDarkTheme
                  ? 'border-amber-500/30 bg-amber-500/10 text-slate-200'
                  : 'border-indigo-100 bg-indigo-50/70 text-indigo-950'
              }`}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Building2 className={`h-4 w-4 ${theme.isDarkTheme ? 'text-[#c9a24b]' : 'text-indigo-600'}`} />
                  <span className="text-xs font-bold">
                    {T.fopIbanTitle}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    haptic('light')
                    setShowFopModal(true)
                  }}
                  className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg transition shadow-xs ${
                    theme.isDarkTheme
                      ? 'bg-[#c9a24b] text-[#17120f] hover:bg-[#d8b25c]'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                >
                  <span>{T.viewFopRequisites}</span>
                  <ChevronRight className="h-3 w-3" />
                </button>
              </div>
              <p className="text-[11px] opacity-80 leading-relaxed">
                {T.fopPaymentInfo}
              </p>
            </div>
          ) : (
            <div
              className={`rounded-2xl border p-3.5 space-y-1.5 transition ${
                theme.isDarkTheme
                  ? 'border-amber-500/30 bg-amber-500/10 text-slate-200'
                  : 'border-amber-200 bg-amber-50/70 text-amber-950'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-base">🤝</span>
                <span className="text-xs font-bold">
                  {lang === 'en' ? 'Arrangement with the master' : 'Оплата за домовленістю з майстром'}
                </span>
              </div>
              <p className="text-[11px] opacity-85 leading-relaxed">
                {lang === 'en'
                  ? 'After placing the order on the website, we will contact you via your phone number to confirm specifications, lead time, and payment details.'
                  : 'Після оформлення замовлення на сайті ми зв’яжемося з вами за вказаним номером телефону для уточнення комплектації, термінів виготовлення та способу оплати.'}
              </p>
            </div>
          )
        )}

        {methods.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-400">
            <span className="inline-flex items-center gap-1">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
              {T.securePayment}
            </span>
            <span className="text-slate-600">•</span>
            <span>{methods.map((m) => paymentLabel(m, lang, theme.isKlodchik)).join(' · ')}</span>
          </div>
        )}
      </div>

      <FopRequisitesModal
        isOpen={showFopModal}
        onClose={() => setShowFopModal(false)}
      />

      {submitMutation.error && !(submitMutation.error.message in fieldErrors) && (
        <p className="mt-4 text-sm text-rose-500">{submitMutation.error.message}</p>
      )}

      {/* Sticky footer */}
      {(() => {
        const discountAmount =
          promoValidation?.valid && promoValidation.code === promo.trim()
            ? promoValidation.discount_percent
              ? Math.round(cart.total_uah * (promoValidation.discount_percent / 100))
              : promoValidation.discount_fixed_uah
                ? Math.min(cart.total_uah, Math.round(promoValidation.discount_fixed_uah))
                : 0
            : 0
        const finalTotalUah = Math.max(0, cart.total_uah - discountAmount)

        return (
          <div
            className={`app-bottom-fixed app-bottom-panel border-t p-3 backdrop-blur-md ${
              theme.isDarkTheme
                ? 'border-[#2d3748] bg-[#1a202c]/95'
                : 'border-gray-200 bg-white/95'
            }`}
          >
            <button
              onClick={handleSubmit}
              disabled={submitMutation.isPending}
              className={`w-full flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold transition disabled:opacity-50 ${theme.accentBtnClass}`}
            >
              {submitMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> {T.submitting}
                </>
              ) : (
                T.submit(finalTotalUah)
              )}
            </button>
          </div>
        )
      })()}
    </div>
  )
}
